'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

export function Tabs({
  className,
  ...props
}: ComponentProps<typeof Root>): JSX.Element {
  return (
    <Root
      data-slot='tabs'
      className={cn('flex flex-col gap-6', className)}
      {...props}
    />
  );
}

const tabsListVariants = cva(
  'text-muted-foreground inline-flex h-9 w-fit items-center justify-center p-[3px]',
  {
    variants: {
      variant: {
        default: cn('bg-muted rounded-lg'),
        underline: cn(
          'bg-transparent px-0 justify-start h-auto border-b gap-6 md:gap-8 w-full pt-0',
        ),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export function TabsList({
  variant,
  className,
  ...props
}: ComponentProps<typeof List> &
  VariantProps<typeof tabsListVariants>): JSX.Element {
  return (
    <List
      data-slot='tabs-list'
      className={cn(
        tabsListVariants({
          variant,
        }),
        className,
      )}
      {...props}
    />
  );
}

const tabsTriggerVariants = cva('', {
  variants: {
    variant: {
      default: cn(
        'data-[state=active]:bg-background dark:data-[state=active]:text-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 data-[state=active]:shadow-sm',
        'px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow]',
        'text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:outline-1',
        'disabled:pointer-events-none disabled:opacity-50',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      ),
      underline: cn(
        'max-w-[max-content] cursor-pointer relative py-4 text-md font-medium transition-colors leading-[130%] text-carbon-500',
        'data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:before:opacity-100',
        "before:content-[''] before:absolute before:bottom-[-4px] before:left-0 before:w-full before:h-1 before:bg-illicitGreen-500 before:rounded-t-xl before:opacity-0",
      ),
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export function TabsTrigger({
  variant,
  className,
  ...props
}: ComponentProps<typeof Trigger> &
  VariantProps<typeof tabsTriggerVariants>): JSX.Element {
  return (
    <Trigger
      data-slot='tabs-trigger'
      className={cn(
        tabsTriggerVariants({
          variant,
        }),
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({
  className,
  ...props
}: ComponentProps<typeof Content>): JSX.Element {
  return (
    <Content
      data-slot='tabs-content'
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}
